import type { BeforeSendFn } from "posthog-js";
import { PostHogProvider as PostHogProviderBase } from "posthog-js/react";

import { sanitizeAnalyticsUrl } from "./postHogUrl";

const redactSensitiveUrls: BeforeSendFn = event => {
  if (!event?.properties) {
    return event;
  }

  // PostHog attaches the current URL to every event. Auth callbacks,
  // verification, invite, and shared links can contain bearer values.
  const urlProperties = [
    "$current_url",
    "$initial_current_url",
    "$referrer",
    "$initial_referrer",
    "$prev_pageview_url",
    "$pathname",
    "$prev_pageview_pathname",
  ];
  for (const propertySet of [
    event.properties,
    event.$set,
    event.$set_once,
    event.properties.$set,
    event.properties.$set_once,
  ]) {
    if (!propertySet || typeof propertySet !== "object" || Array.isArray(propertySet)) {
      continue;
    }
    const properties = propertySet as Record<string, unknown>;
    for (const key of urlProperties) {
      if (typeof properties[key] === "string") {
        properties[key] = sanitizeAnalyticsUrl(properties[key]);
      }
    }
  }
  return event;
};

export const PostHogProvider = ({ children }: { children: React.ReactNode }) => {
  const apiKey = import.meta.env.REACT_APP_PUBLIC_POSTHOG_KEY;

  // Skip PostHog initialization if no API key is configured (e.g., local development)
  if (!apiKey) {
    return <>{children}</>;
  }

  const options = {
    api_host: import.meta.env.REACT_APP_PUBLIC_POSTHOG_HOST,
    before_send: redactSensitiveUrls,
    capture_pageview: "history_change" as const,
    debug: import.meta.env.REACT_APP_POSTHOG_DEBUG === "true",
  };

  return (
    <PostHogProviderBase apiKey={apiKey} options={options}>
      {children}
    </PostHogProviderBase>
  );
};
